import { alpha, Box } from "@mui/material";
import type { FC, ReactNode } from "react";
import { ALPHA, EVENT_LIST, FONT_SIZE, ICON_TILE, INK, LINE_HEIGHT } from "@/theme/tokens";

type Props = {
	/** A marker for what kind of event it was — an `AccentIconTile` of size `xs`, whose width the column is sized to. */
	leading: ReactNode;
	/** What happened, in a few words. */
	title: ReactNode;
	/** One sentence of detail. Bold runs (`<b>`) are set in the primary ink. Absent = none. */
	text?: ReactNode;
	/** When, and any small print after it. Absent = none. */
	meta?: ReactNode;
	/** Pinned to the row's top-trailing corner (a count pill). Absent = none. */
	trailing?: ReactNode;
	/** The step that stands now (a timeline's latest): its tile carries a soft brand halo. Absent = a plain step. */
	emphasis?: boolean;
	dataTestId?: string;
};

/** One row of an `EventList`: marker, title, detail and when, with an optional trailing pill. */
export const EventListItem: FC<Props> = ({ leading, title, text, meta, trailing, emphasis, dataTestId }) => (
	<Box
		component="li"
		data-testid={dataTestId}
		data-emphasis={emphasis === true}
		sx={{ display: "grid", gridTemplateColumns: `${ICON_TILE.XS.size}px minmax(0, 1fr) auto`, columnGap: 1.25, alignItems: "start", py: 1 }}
	>
		<Box
			sx={(theme) => ({
				display: "flex",
				borderRadius: `${ICON_TILE.XS.size / 4}px`,
				...(emphasis === true && { boxShadow: `0 0 0 ${EVENT_LIST.EMPHASIS_RING_PX}px ${alpha(theme.palette.primary.main, ALPHA.CONTROL_TINT_HOVER)}` }),
			})}
		>
			{leading}
		</Box>
		<Box sx={{ minWidth: 0 }}>
			<Box sx={{ fontSize: FONT_SIZE.SECONDARY, fontWeight: 600, color: INK.PRIMARY, lineHeight: LINE_HEIGHT.SNUG }}>{title}</Box>
			{text !== undefined && (
				<Box
					sx={{
						fontSize: FONT_SIZE.CAPTION,
						color: INK.SECONDARY,
						lineHeight: LINE_HEIGHT.SNUG,
						mt: 0.125,
						"& b, & strong": { color: INK.PRIMARY, fontWeight: 600 },
					}}
				>
					{text}
				</Box>
			)}
			{meta !== undefined && <Box sx={{ fontSize: FONT_SIZE.LABEL_SM, color: INK.MUTED, mt: 0.375 }}>{meta}</Box>}
		</Box>
		{trailing !== undefined ? <Box sx={{ alignSelf: "start" }}>{trailing}</Box> : <span />}
	</Box>
);
